import { SharedPermissions } from '@lawyer/shared';

/**
 * Может ли этот человек **дойти до раздела** — вопрос про capability, а не про доступ к объекту.
 *
 * ## Два разных вопроса, и путать их дорого
 *
 * `SharedPermissions.can(view, key, aclLevel)` отвечает на вопрос **о конкретном объекте**: «вправе
 * ли этот человек сделать это с этим актом». Ответ — конъюнкция: нужна и возможность (`act:read`
 * выдан ролью), и уровень доступа к самому объекту (`VIEWER` и выше по его ACL). Ключ, у которого в
 * каталоге стоит `requiredLevel`, без переданного уровня получает отказ — намеренно и правильно:
 * «уровень не передан» не то же самое, что «уровень есть» (`can.util.ts`, «not passed is not
 * allowed»).
 *
 * У **раздела** объекта нет. За `/acts` стоит не акт, а реестр: полторы тысячи записей, каждая со
 * своим ACL, и фильтрует их сервер одним SQL-запросом внутри use-case (`rules/permissions.mdc` §8).
 * Предъявить уровень доступа на входе в раздел не может никто, включая владельца организации, —
 * предъявлять нечего. Поэтому вопрос здесь другой: «есть ли у человека такая возможность вообще,
 * где-нибудь в организации», то есть ровно слои 1–3 лестницы прав.
 *
 * Тот же самый вопрос и теми же словами задаёт сервер: `authorizeCapability` в
 * `server/src/domain/access/authorize.util.ts` — слой быстрого отказа перед use-case, который
 * решает уже по ACL. Клиентский гард раздела обязан спрашивать не строже сервера: иначе раздел
 * закрыт для всех, кому он открыт, — что и произошло с `/acts`, `/contracts` и `/correspondence`.
 *
 * ## Чем это **не** является
 *
 * Ответом про документ. К карточке акта, кнопке «изменить» и любому элементу, у которого есть
 * `resourceId`, применяется `can()` с уровнем — и никогда эта функция: она про capability и по
 * построению не смотрит на ACL. Отдельное имя существует для того, чтобы разница была видна в месте
 * вызова, а не выводилась из докстринга: голый `effectivePermission` по месту в компоненте выглядел
 * бы как «упрощённый can()» и однажды им бы и стал.
 *
 * И — как и `can()` — подсказка, а не механизм безопасности (`rules/permissions.mdc` §11). Каждый
 * запрос, который сделает открывшийся экран, сервер авторизует заново.
 *
 * Fail-closed по тем же двум причинам, что и `can()`: ключ вне каталога — «нет» (опечатка ничего не
 * открывает), персональный DENY сильнее выдачи ролью, владелец обходит и то и другое (§9).
 */
export const canReachSection = (
  view: SharedPermissions.CapabilityView,
  permission: string,
): boolean =>
  SharedPermissions.isPermissionKey(permission) &&
  SharedPermissions.effectivePermission(view, permission);
